// Named themes (docs/architecture.md §17a). Pure data: used by the Vite plugin that generates the
// theme CSS, by the theme picker's previews, and by the contrast test. Every pair a user reads
// must pass WCAG 2.2 AA (4.5:1), which `themes.test.ts` enforces.

export type ThemeId =
  | 'lighthouse'
  | 'harbour-dawn'
  | 'iceberg-alley'
  | 'jellybean-row'
  | 'fogbank'
  | 'northern-lights'
  | 'midnight-tide';

export type ThemeChoice = ThemeId | 'system';

export interface ThemeTokens {
  scheme: 'light' | 'dark';
  bg: string;
  surface: string;
  subtle: string;
  border: string;
  text: string;
  muted: string;
  primary: string;
  onPrimary: string;
  accent: string;
  onAccent: string;
  ring: string;
  success: string;
  warning: string;
  danger: string;
  info: string;
  /** Decorative glows for backdrops and highlights; never used for text. */
  glow: [string, string, string];
}

const LIGHT_STATUS = { success: '#166B45', warning: '#8A5200', danger: '#B42318', info: '#1D5FA8' };
const DARK_STATUS = { success: '#4CC38A', warning: '#F2B65A', danger: '#FF8A8A', info: '#8CC2FF' };

export const THEMES: Record<ThemeId, ThemeTokens> = {
  lighthouse: {
    scheme: 'light',
    bg: '#F6F4EF',
    surface: '#FFFFFF',
    subtle: '#EFECE5',
    border: '#E2DDD3',
    text: '#14202E',
    muted: '#566170',
    primary: '#1B3A5C',
    onPrimary: '#FFFFFF',
    accent: '#F2A93B',
    onAccent: '#1B2A3A',
    ring: '#2F6FB0',
    ...LIGHT_STATUS,
    glow: ['#F2A93B', '#8DB8E8', '#1B3A5C'],
  },
  'harbour-dawn': {
    scheme: 'light',
    bg: '#F3F8F7',
    surface: '#FFFFFF',
    subtle: '#E6F0EE',
    border: '#D3E3E0',
    text: '#0F2E2C',
    muted: '#4A6260',
    primary: '#0B625D',
    onPrimary: '#FFFFFF',
    accent: '#E8735A',
    onAccent: '#2B0E07',
    ring: '#0B625D',
    ...LIGHT_STATUS,
    glow: ['#E8735A', '#F7C59F', '#0E6B66'],
  },
  'iceberg-alley': {
    scheme: 'light',
    bg: '#F4F8FB',
    surface: '#FFFFFF',
    subtle: '#E8F1F7',
    border: '#D3E2ED',
    text: '#0D2131',
    muted: '#4F6578',
    primary: '#1A5A85',
    onPrimary: '#FFFFFF',
    accent: '#6EC1E4',
    onAccent: '#0A2738',
    ring: '#1A5A85',
    ...LIGHT_STATUS,
    glow: ['#9AD3EC', '#CFE8F5', '#6EC1E4'],
  },
  'jellybean-row': {
    scheme: 'light',
    bg: '#FFFAF3',
    surface: '#FFFFFF',
    subtle: '#FFF0E0',
    border: '#F1DFCB',
    text: '#22162B',
    muted: '#655570',
    primary: '#B8124F',
    onPrimary: '#FFFFFF',
    accent: '#FFB300',
    onAccent: '#2A1A00',
    ring: '#5C6BC0',
    ...LIGHT_STATUS,
    glow: ['#2EC4B6', '#FF6B6B', '#FFB300'],
  },
  fogbank: {
    scheme: 'light',
    bg: '#F2F3F5',
    surface: '#FFFFFF',
    subtle: '#E8EAED',
    border: '#D8DCE1',
    text: '#1B2027',
    muted: '#58616C',
    primary: '#3A4757',
    onPrimary: '#FFFFFF',
    accent: '#8C9CB0',
    onAccent: '#10161E',
    ring: '#3A4757',
    ...LIGHT_STATUS,
    glow: ['#C9D1DB', '#AEB9C6', '#E6E9ED'],
  },
  'northern-lights': {
    scheme: 'dark',
    bg: '#0A0D1D',
    surface: '#11162C',
    subtle: '#191F3D',
    border: '#283156',
    text: '#E9ECFF',
    muted: '#A3AACB',
    primary: '#7CF2C2',
    onPrimary: '#062019',
    accent: '#B98CFF',
    onAccent: '#1A0B33',
    ring: '#7CF2C2',
    ...DARK_STATUS,
    glow: ['#2DE1A4', '#7B5CFF', '#1FB6FF'],
  },
  'midnight-tide': {
    scheme: 'dark',
    bg: '#070D16',
    surface: '#0E1726',
    subtle: '#142035',
    border: '#22314A',
    text: '#E8EDF2',
    muted: '#9AA7B4',
    primary: '#8DB8E8',
    onPrimary: '#071526',
    accent: '#F2B65A',
    onAccent: '#241503',
    ring: '#8DB8E8',
    ...DARK_STATUS,
    glow: ['#F2B65A', '#3E78B2', '#1B3A5C'],
  },
};

export const THEME_IDS = Object.keys(THEMES) as ThemeId[];

/** What `system` resolves to by day and by night. */
export const SYSTEM_THEMES = {
  light: 'lighthouse',
  dark: 'midnight-tide',
} as const satisfies Record<'light' | 'dark', ThemeId>;

export const DEFAULT_THEME: ThemeChoice = 'system';

/** CSS custom properties for every theme, plus `color-scheme`. Generated into the bundle. */
export function themesCss(): string {
  return THEME_IDS.map((id) => {
    const t = THEMES[id];
    const vars = Object.entries(t)
      .filter(([key]) => key !== 'scheme' && key !== 'glow')
      .map(([key, value]) => `--${kebab(key)}:${value as string};`)
      .join('');
    const glows = t.glow.map((g, i) => `--glow-${i + 1}:${g};`).join('');
    return `[data-theme="${id}"]{color-scheme:${t.scheme};${vars}${glows}}`;
  }).join('\n');
}

function kebab(s: string): string {
  return s.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}
